import { useRef, useState } from "react";

import WalletPanel from "components/Ui/WalletPanel/WalletPanel";
import {
    useVendorWallet,
    useTopUpVendorWallet,
    useDeductVendorWallet,
} from "hooks/useVendorWallet";
import { formatMoney } from "utils/money";

/**
 * محفظة المطعم (api#147, admin#41) — the restaurant's data and words for the
 * shared WalletPanel. The admin opens it from the restaurants table
 * (`canTopUp`: شحن and خصم); the restaurant opens it from its own «المحفظة»
 * page, read-only.
 */

/** What each restaurant movement is called. A negative adjustment reads «خصم». */
const VENDOR_TYPE_LABELS = {
    topup: "شحن رصيد",
    order_charge: "عمولة طلب",
    order_refund: "استرجاع عمولة",
    adjustment: "تسوية",
};

/** A retry key, made once per attempt. */
const newIdempotencyKey = () =>
    globalThis.crypto?.randomUUID?.() ??
    `wallet-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * The notices, in the order they matter: the closure first — it is why the
 * restaurant takes no orders — then the payment mode.
 *
 * @param {import("services/vendorWallet").VendorWalletSummary|null} wallet
 */
const buildVendorNotices = (wallet) => {
    if (!wallet) return [];
    const notices = [];
    const balance = Number(wallet.balance ?? 0);

    if (wallet.blocked) {
        notices.push({
            tone: "danger",
            title: wallet.blocked_reason,
            text:
                "يجب أن يكون رصيد المحفظة أكبر من صفر لفتح المطعم." +
                (balance <= 0
                    ? ` المطلوب لإعادة الفتح: أكثر من ${formatMoney(Math.abs(balance))}.`
                    : ""),
            hint: "الطلبات الحالية مستمرة وتُطبع كالمعتاد. الشحن يرفع الإغلاق فقط، ولا يفتح المطعم تلقائياً.",
        });
    }

    if (!wallet.enabled) {
        notices.push({
            tone: "info",
            text: "هذا المطعم على النظام النقدي: العمولة تُحسب في كشف الحساب، ولا تُخصم من المحفظة.",
        });
    } else if (!wallet.effective) {
        notices.push({
            tone: "info",
            text: "المحفظة غير مفعلة في هذه المدينة، فلا تُخصم أي عمولة من الرصيد حالياً.",
        });
    }

    return notices;
};

/**
 * @param {object} props
 * @param {number|string} props.userId The restaurant's users.id.
 * @param {boolean} [props.canTopUp] Admin only: شحن and خصم.
 * @param {boolean} [props.refreshOnFocus] For the restaurant's own screen.
 * @param {Function} [props.showMessage]
 */
const VendorWalletPanel = ({ userId, canTopUp = false, refreshOnFocus = false, showMessage }) => {
    const [page, setPage] = useState(1);
    // THE SAME KEY FOR EVERY TRY OF ONE MOVEMENT. If the response is lost and
    // the operator presses again, the server sees the same key and moves the
    // money once. A new key is made only after the server confirmed.
    const idempotencyKey = useRef(newIdempotencyKey());

    const { wallet, transactions, pages, loading, isFetching, error, refetch } = useVendorWallet(
        userId,
        page,
        {
            refreshOnFocus,
        },
    );
    const topUp = useTopUpVendorWallet();
    const deduct = useDeductVendorWallet();

    const handleTopUp = async ({ amount, note }) => {
        try {
            const result = await topUp.mutateAsync({
                userId,
                amount,
                note,
                idempotencyKey: idempotencyKey.current,
            });
            idempotencyKey.current = newIdempotencyKey();

            const balanceText = `الرصيد الآن ${formatMoney(result.wallet?.balance)}`;
            showMessage?.(
                "success",
                result.duplicate ? "تمت معالجة الشحن سابقاً" : "تم الشحن",
                result.wallet?.blocked
                    ? `${balanceText}. ما زال المطعم مغلقاً: يجب أن يكون الرصيد أكبر من صفر.`
                    : `${balanceText}. الشحن لا يفتح المطعم تلقائياً.`,
            );
            return true;
        } catch (err) {
            // The key is kept: a retry of THIS top-up must reuse it.
            showMessage?.(
                "error",
                "تعذر الشحن",
                err.response?.data?.message || "حدث خطأ غير متوقع",
            );
            return false;
        }
    };

    const handleDeduct = async ({ amount, note }) => {
        try {
            const result = await deduct.mutateAsync({
                userId,
                amount,
                note,
                idempotencyKey: idempotencyKey.current,
            });
            idempotencyKey.current = newIdempotencyKey();

            const balanceText = `الرصيد الآن ${formatMoney(result.wallet?.balance)}`;
            showMessage?.(
                "success",
                result.duplicate ? "تمت معالجة الخصم سابقاً" : "تم الخصم",
                result.wallet?.blocked
                    ? `${balanceText}. أُغلق المطعم بسبب رصيد المحفظة.`
                    : balanceText,
            );
            return true;
        } catch (err) {
            // The key is kept: a retry of THIS deduction must reuse it.
            showMessage?.(
                "error",
                "تعذر الخصم",
                err.response?.data?.message || "حدث خطأ غير متوقع",
            );
            return false;
        }
    };

    return (
        <WalletPanel
            balance={wallet ? wallet.balance : null}
            notices={buildVendorNotices(wallet)}
            transactions={transactions}
            typeLabels={VENDOR_TYPE_LABELS}
            page={page}
            pages={pages}
            onPageChange={setPage}
            loading={loading}
            isFetching={isFetching}
            error={error}
            onRetry={refetch}
            canTopUp={canTopUp}
            onTopUp={handleTopUp}
            onDeduct={canTopUp ? handleDeduct : undefined}
            topUpPending={topUp.isPending || deduct.isPending}
            zeroWarning={
                wallet?.effective
                    ? "بعد هذا الخصم يصبح الرصيد صفراً أو أقل، فيُغلق المطعم ولا يستقبل طلبات جديدة حتى يُشحن."
                    : ""
            }
            showMessage={showMessage}
            linkOrders={canTopUp}
        />
    );
};

export default VendorWalletPanel;
